import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import {
  Plus, Trash, TrashSimple, X, GraduationCap, CaretDown, CaretUp, Copy, LockKey, MagicWand,
} from "@phosphor-icons/react";
import { SmartDateField, formatDateDisplay } from "@/components/ResourceSection";

/**
 * EducationSection — vault-native UX for Education.
 *
 * Replaces the generic ResourceSection wrapper for this resource. Key principles:
 *   1. ALWAYS editable inline. No "view" vs "edit" modes. No modal. No pencil icon.
 *   2. Subjects visible by default. Add / repeat / delete are explicit text-labelled buttons.
 *   3. Debounced auto-save. The user types — we PUT 800ms after their last keystroke.
 *   4. Cards are collapsible (chevron) but expanded by default.
 *   5. Single "+ Add education" button at the top of the section.
 */

const LEVEL_OPTIONS = [
  "GCSE", "A-Level", "AS-Level", "BTEC", "T-Level", "NVQ", "IB", "Module", "Other",
];

// Levels whose rows collect university-transcript-style extras (module code,
// mark, result, credits, attempt). Everything else stays a minimal
// subject/grade/year/level row so GCSEs don't get a wall of columns.
const RICH_LEVELS = new Set(["Module", "BTEC", "T-Level", "IB", "NVQ"]);
const isRichLevel = (lvl) => RICH_LEVELS.has(lvl);

const CLASSIFICATION_OPTIONS = [
  "", "First", "2:1", "2:2", "Third", "Distinction", "Merit", "Pass", "Fail",
];
const MODE_OPTIONS = ["", "Full Time", "Part Time", "Distance Learning", "Sandwich"];

function blank() {
  return {
    qualification: "",
    provider: "",
    location: "",
    start_date: "",
    end_date: "",
    subjects: [],
    classification: "",
    final_mark: "",
    credits_achieved: "",
    mode_of_study: "",
  };
}

// Debounce helper — returns a function that fires after `delay`ms of no calls.
// Exposes `.cancel()` so the caller can flush a pending invocation when doing
// an explicit immediate save (e.g. the "lock it in" button).
function useDebounced(fn, delay = 800) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  // Cleanup any queued save on unmount so we don't PUT to a deleted entry id.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

// ---------- One Education entry card (always inline-editable) ----------
function EducationCard({ entry, onChange, onDelete, onCollapseToggle, collapsed }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractText, setExtractText] = useState("");
  const [extractLevel, setExtractLevel] = useState("GCSE");
  const [extracting, setExtracting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);

  const persist = useDebounced(async (next) => {
    setSaving(true);
    try {
      await api.put(`/education/${next.id}`, next);
      setDirty(false);
      setJustSaved(true);
      // Fade the "saved" tag after 2.5s so it doesn't permanently stamp the card.
      setTimeout(() => setJustSaved(false), 2500);
    } catch (e) {
      toast.error("Couldn't save changes");
    } finally { setSaving(false); }
  }, 700);

  /**
   * "Lock it in" — explicit user-driven commit. Flushes any pending debounced
   * save immediately, shows a vault-flavoured toast, and collapses the card
   * so the user gets a clear "this is locked in my vault now" signal.
   */
  const lockIn = async () => {
    persist.cancel?.();   // flush any pending debounced save — avoid double-PUT
    setSaving(true);
    try {
      await api.put(`/education/${entry.id}`, entry);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      onCollapseToggle?.(true);
    } catch {
      toast.error("Couldn't lock it in — try again");
    } finally { setSaving(false); }
  };

  const update = (patch) => {
    const next = { ...entry, ...patch };
    onChange(next);
    setDirty(true);
    persist(next);
  };

  const updateSubject = (idx, patch) => {
    const subjects = (entry.subjects || []).slice();
    subjects[idx] = { ...subjects[idx], ...patch };
    update({ subjects });
  };
  const addSubject = () => {
    const subs = entry.subjects || [];
    const last = subs[subs.length - 1];
    update({ subjects: [...subs, { subject: "", grade: "", year: last?.year || "", level: last?.level || "GCSE" }] });
  };
  const repeatSubject = (idx) => {
    const src = (entry.subjects || [])[idx] || {};
    update({ subjects: [...(entry.subjects || []), { subject: "", grade: src.grade || "", year: src.year || "", level: src.level || "GCSE" }] });
  };
  const removeSubject = (idx) => {
    update({ subjects: (entry.subjects || []).filter((_, i) => i !== idx) });
  };

  const extractSubjects = async () => {
    const txt = extractText.trim();
    if (txt.length < 3) {
      toast.error("Paste some subject text first");
      return;
    }
    setExtracting(true);
    try {
      const { data } = await api.post("/ai/extract-subjects", {
        text: txt,
        default_level: extractLevel || "GCSE",
      });
      const rows = (data?.subjects || []).map((s) => ({
        subject: s.subject || "",
        grade:   s.grade   || "",
        year:    s.year    || "",
        level:   s.level   || extractLevel || "GCSE",
        module_code: s.module_code || "",
        mark:        s.mark || "",
        result:      s.result || "",
        credits:     s.credits || "",
        attempt:     s.attempt || "",
      }));
      const qual = data?.qualification || {};
      // Merge qualification-wide fields the AI extracted (only if the current
      // entry hasn't already got those set — don't overwrite user's input)
      const patch = { subjects: [...(entry.subjects || []), ...rows] };
      if (qual.classification && !entry.classification)     patch.classification    = qual.classification;
      if (qual.final_mark && !entry.final_mark)             patch.final_mark        = qual.final_mark;
      if (qual.credits_achieved && !entry.credits_achieved) patch.credits_achieved  = qual.credits_achieved;
      if (qual.mode_of_study && !entry.mode_of_study)       patch.mode_of_study     = qual.mode_of_study;
      if (rows.length === 0) {
        toast.error("Couldn't find any subjects in that text");
        return;
      }
      update(patch);
      toast.success(`Added ${rows.length} subject${rows.length === 1 ? "" : "s"}`);
      setExtractText("");
      setExtractOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const extractFromDocument = async (file) => {
    if (!file) return;
    const isPdf   = /pdf$/i.test(file.name) || file.type === "application/pdf";
    const isDocx  = /docx$/i.test(file.name) || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const isImage = /\.(jpe?g|png|webp)$/i.test(file.name) || /^image\/(jpeg|png|webp)$/i.test(file.type);
    if (!isPdf && !isDocx && !isImage) {
      toast.error("Upload a PDF, DOCX, or photo (JPG/PNG/WEBP) of your transcript / certificate");
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("default_level", extractLevel || "Module");
      const { data } = await api.post("/ai/extract-subjects/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { default_level: extractLevel || "Module" },
      });
      const rows = (data?.subjects || []).map((s) => ({
        subject: s.subject || "",
        grade:   s.grade   || "",
        year:    s.year    || "",
        level:   s.level   || extractLevel || "Module",
        module_code: s.module_code || "",
        mark:        s.mark || "",
        result:      s.result || "",
        credits:     s.credits || "",
        attempt:     s.attempt || "",
      }));
      const qual = data?.qualification || {};
      if (rows.length === 0 && !qual.classification && !qual.final_mark) {
        toast.error("Couldn't find any subjects in that document");
        return;
      }
      const patch = { subjects: [...(entry.subjects || []), ...rows] };
      if (qual.classification && !entry.classification)     patch.classification    = qual.classification;
      if (qual.final_mark && !entry.final_mark)             patch.final_mark        = qual.final_mark;
      if (qual.credits_achieved && !entry.credits_achieved) patch.credits_achieved  = qual.credits_achieved;
      if (qual.mode_of_study && !entry.mode_of_study)       patch.mode_of_study     = qual.mode_of_study;
      update(patch);
      const savedNote = data?.saved_document_id ? " · saved to Document Safe" : "";
      const ocrNote = data?.ocr_used ? " · OCR'd" : "";
      toast.success(`Extracted ${rows.length} row${rows.length === 1 ? "" : "s"} from ${file.name}${ocrNote}${savedNote}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't parse that document");
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  const subjectCount = (entry.subjects || []).length;
  const headline = entry.qualification || "Untitled programme";
  const summary = [
    entry.provider, entry.location,
    `${formatDateDisplay(entry.start_date)} — ${formatDateDisplay(entry.end_date)}`.trim() !== "—" ?
      `${formatDateDisplay(entry.start_date)} — ${formatDateDisplay(entry.end_date)}` : null,
    subjectCount ? `${subjectCount} subject${subjectCount === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" · ");

  const copyAll = () => {
    const head = `${entry.qualification || ""} · ${entry.provider || ""}` +
      (entry.location ? `\n${entry.location}` : "") +
      `\n${formatDateDisplay(entry.start_date)} — ${formatDateDisplay(entry.end_date)}`;
    if (!subjectCount) { copyText(head, "Entry copied"); return; }
    const rows = entry.subjects
      .filter((s) => s.subject || s.grade || s.year || s.level)
      .map((s) => `  - ${s.subject || "?"} · ${s.grade || "—"} · ${s.year || "—"} · ${s.level || "—"}`)
      .join("\n");
    copyText(`${head}\n\nSubjects:\n${rows}`, "Entry copied");
  };

  return (
    <div className="card-hard rounded-md bg-white overflow-hidden" data-testid={`edu-card-${entry.id}`}>
      {/* Header — collapsible */}
      <div className="flex items-center justify-between gap-3 p-3 border-b-[1.5px] border-[var(--brand-ink)]/10 bg-[var(--brand-bg)]">
        <button onClick={onCollapseToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-70 transition"
          data-testid={`edu-collapse-${entry.id}`}>
          {collapsed ? <CaretDown size={16} weight="bold"/> : <CaretUp size={16} weight="bold"/>}
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{headline}</p>
            <p className="text-xs font-mono-d text-[var(--brand-muted)] truncate">{summary || "no details yet"}</p>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {saving && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">saving…</span>}
          {!saving && dirty && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">unsaved</span>}
          {!saving && !dirty && justSaved && <span className="font-mono-d text-[10px] text-[var(--bolt-sage)]">saved</span>}
          {/* Only show "saved" once the user has actually edited something this session
              — avoids showing a misleading "saved" tag on a card the user never touched. */}
          <button onClick={copyAll} data-testid={`edu-copy-${entry.id}`}
            className="btn-hard bg-white rounded px-2 py-1 text-xs flex items-center gap-1">
            <Copy size={11}/> copy
          </button>
          <button onClick={onDelete} data-testid={`edu-delete-${entry.id}`}
            className="btn-hard bg-white rounded px-2 py-1 text-xs flex items-center gap-1 hover:bg-[var(--pastel-peach)]">
            <Trash size={11}/> delete
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Top row — institution-level fields, all inline-editable */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="label-tiny block mb-1">Programme</span>
              <input data-testid={`edu-qual-${entry.id}`}
                value={entry.qualification || ""}
                onChange={(e) => update({ qualification: e.target.value })}
                placeholder="e.g. GCSEs, A-Levels, BSc Politics"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <label className="block">
              <span className="label-tiny block mb-1">Institution</span>
              <input data-testid={`edu-provider-${entry.id}`}
                value={entry.provider || ""}
                onChange={(e) => update({ provider: e.target.value })}
                placeholder="e.g. Riddlesdown Collegiate"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <label className="block">
              <span className="label-tiny block mb-1">Town / city</span>
              <input data-testid={`edu-location-${entry.id}`}
                value={entry.location || ""}
                onChange={(e) => update({ location: e.target.value })}
                placeholder="e.g. Croydon"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="label-tiny block mb-1">From</span>
                <SmartDateField
                  resource="education" fieldKey="start_date"
                  value={entry.start_date || ""}
                  onChange={(v) => update({ start_date: v })}
                />
              </div>
              <div>
                <span className="label-tiny block mb-1">To</span>
                <SmartDateField
                  resource="education" fieldKey="end_date"
                  value={entry.end_date || ""}
                  onChange={(v) => update({ end_date: v })}
                />
              </div>
            </div>
          </div>

          {/* Degree / transcript-wide details — optional strip for university,
              BTEC, T-Level etc. Auto-open if any of the fields are populated
              (so users see them after import) or if any subject is a rich
              level. Otherwise collapsed by default to keep GCSE entries clean. */}
          {(() => {
            const hasDegreeFields = !!(entry.classification || entry.final_mark || entry.credits_achieved || entry.mode_of_study);
            const anyRichSubject = (entry.subjects || []).some((s) => isRichLevel(s.level));
            const defaultOpen = hasDegreeFields || anyRichSubject;
            return (
              <details data-testid={`edu-degree-details-${entry.id}`} open={defaultOpen || undefined}
                className="border-t-[1.5px] border-[var(--brand-ink)]/10 pt-2">
                <summary className="flex items-center gap-2 cursor-pointer select-none">
                  <p className="label-tiny m-0">degree / transcript details</p>
                  <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">
                    classification, final mark, credits — for BSc / MSc / BTEC / T-Level etc.
                  </span>
                </summary>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  <label className="block">
                    <span className="label-tiny block mb-1">Classification</span>
                    <select
                      data-testid={`edu-class-${entry.id}`}
                      value={entry.classification || ""}
                      onChange={(e) => update({ classification: e.target.value })}
                      className="w-full card-hard rounded-md px-2 py-1.5 bg-white text-sm">
                      {CLASSIFICATION_OPTIONS.map((o) => <option key={o || "none"} value={o}>{o || "—"}</option>)}
                      {entry.classification && !CLASSIFICATION_OPTIONS.includes(entry.classification) && (
                        <option value={entry.classification}>{entry.classification}</option>
                      )}
                    </select>
                  </label>
                  <label className="block">
                    <span className="label-tiny block mb-1">Final mark</span>
                    <input
                      data-testid={`edu-final-mark-${entry.id}`}
                      value={entry.final_mark || ""}
                      onChange={(e) => update({ final_mark: e.target.value })}
                      placeholder="e.g. 58.58"
                      inputMode="decimal"
                      className="w-full card-hard rounded-md px-2.5 py-1.5 bg-white text-sm"/>
                  </label>
                  <label className="block">
                    <span className="label-tiny block mb-1">Credits achieved</span>
                    <input
                      data-testid={`edu-credits-${entry.id}`}
                      value={entry.credits_achieved || ""}
                      onChange={(e) => update({ credits_achieved: e.target.value })}
                      placeholder="e.g. 180"
                      inputMode="numeric"
                      className="w-full card-hard rounded-md px-2.5 py-1.5 bg-white text-sm"/>
                  </label>
                  <label className="block">
                    <span className="label-tiny block mb-1">Mode of study</span>
                    <select
                      data-testid={`edu-mode-${entry.id}`}
                      value={entry.mode_of_study || ""}
                      onChange={(e) => update({ mode_of_study: e.target.value })}
                      className="w-full card-hard rounded-md px-2 py-1.5 bg-white text-sm">
                      {MODE_OPTIONS.map((o) => <option key={o || "none"} value={o}>{o || "—"}</option>)}
                    </select>
                  </label>
                </div>
              </details>
            );
          })()}

          {/* Subjects sub-table — always visible, always editable */}
          <div className="pt-2 border-t-[1.5px] border-[var(--brand-ink)]/10">
            <div className="flex items-center justify-between mb-2">
              <p className="label-tiny m-0">GCSEs / A-Levels / modules</p>
              <p className="font-mono-d text-[10px] text-[var(--brand-muted)]">
                one row per subject — use the repeat button to copy a row&apos;s grade/year/level into a new one
              </p>
            </div>
            {subjectCount > 0 && (
              <div className="grid grid-cols-[1fr_90px_90px_140px_110px] gap-2 px-1 mb-1.5">
                <span className="label-tiny">subject</span>
                <span className="label-tiny text-center">grade</span>
                <span className="label-tiny text-center">year</span>
                <span className="label-tiny">level</span>
                <span/>
              </div>
            )}
            <div className="space-y-2">
              {(entry.subjects || []).map((s, i) => {
                const rich = isRichLevel(s.level);
                return (
                <div key={i} data-testid={`edu-subj-${entry.id}-${i}`} className={rich ? "card-hard rounded-md bg-[var(--brand-bg)] p-2" : ""}>
                  <div className="grid grid-cols-[1fr_90px_90px_140px_110px] gap-2">
                    <input value={s.subject || ""}
                      data-testid={`edu-subj-name-${entry.id}-${i}`}
                      onChange={(e) => updateSubject(i, { subject: e.target.value })}
                      placeholder="e.g. English Literature"
                      className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm"/>
                    <input value={s.grade || ""}
                      data-testid={`edu-subj-grade-${entry.id}-${i}`}
                      onChange={(e) => updateSubject(i, { grade: e.target.value })}
                      placeholder="A*"
                      className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"/>
                    <input value={s.year || ""}
                      data-testid={`edu-subj-year-${entry.id}-${i}`}
                      onChange={(e) => updateSubject(i, { year: e.target.value })}
                      placeholder="2015"
                      inputMode="numeric" maxLength={4}
                      className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"/>
                    <select value={s.level || "GCSE"}
                      data-testid={`edu-subj-level-${entry.id}-${i}`}
                      onChange={(e) => updateSubject(i, { level: e.target.value })}
                      className="card-hard rounded-md px-2 py-1.5 bg-white text-sm">
                      {LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      {s.level && !LEVEL_OPTIONS.includes(s.level) && <option value={s.level}>{s.level}</option>}
                    </select>
                    <div className="flex items-center gap-1">
                      <button onClick={() => repeatSubject(i)}
                        data-testid={`edu-subj-rpt-${entry.id}-${i}`}
                        title="repeat this row's grade, year and level"
                        className="btn-hard bg-white rounded px-2 py-1.5 text-[10px] font-mono-d">rpt</button>
                      <button onClick={() => removeSubject(i)}
                        data-testid={`edu-subj-remove-${entry.id}-${i}`}
                        title="remove this subject"
                        className="btn-hard bg-white rounded p-1.5 hover:bg-[var(--pastel-peach)]">
                        <X size={12} weight="bold"/>
                      </button>
                    </div>
                  </div>

                  {rich && (
                    <div className="mt-1.5" data-testid={`edu-subj-rich-${entry.id}-${i}`}>
                      <div className="grid grid-cols-[120px_80px_120px_80px_80px_1fr] gap-2 px-1 mb-1">
                        <span className="label-tiny">module code</span>
                        <span className="label-tiny text-center">mark</span>
                        <span className="label-tiny">result</span>
                        <span className="label-tiny text-center">credits</span>
                        <span className="label-tiny text-center">attempt</span>
                        <span/>
                      </div>
                      <div className="grid grid-cols-[120px_80px_120px_80px_80px_1fr] gap-2">
                        <input value={s.module_code || ""}
                          data-testid={`edu-subj-code-${entry.id}-${i}`}
                          onChange={(e) => updateSubject(i, { module_code: e.target.value })}
                          placeholder="4SSPP101"
                          className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm font-mono-d"/>
                        <input value={s.mark || ""}
                          data-testid={`edu-subj-mark-${entry.id}-${i}`}
                          onChange={(e) => updateSubject(i, { mark: e.target.value })}
                          placeholder="70"
                          inputMode="numeric"
                          className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"/>
                        <select value={s.result || ""}
                          data-testid={`edu-subj-result-${entry.id}-${i}`}
                          onChange={(e) => updateSubject(i, { result: e.target.value })}
                          className="card-hard rounded-md px-2 py-1.5 bg-white text-sm">
                          <option value="">—</option>
                          <option value="Pass">Pass</option>
                          <option value="Merit">Merit</option>
                          <option value="Distinction">Distinction</option>
                          <option value="Fail">Fail</option>
                          {s.result && !["", "Pass", "Merit", "Distinction", "Fail"].includes(s.result) && <option value={s.result}>{s.result}</option>}
                        </select>
                        <input value={s.credits || ""}
                          data-testid={`edu-subj-credits-${entry.id}-${i}`}
                          onChange={(e) => updateSubject(i, { credits: e.target.value })}
                          placeholder="15"
                          inputMode="numeric"
                          className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"/>
                        <input value={s.attempt || ""}
                          data-testid={`edu-subj-attempt-${entry.id}-${i}`}
                          onChange={(e) => updateSubject(i, { attempt: e.target.value })}
                          placeholder="1"
                          inputMode="numeric" maxLength={2}
                          className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"/>
                        <span/>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={addSubject}
                data-testid={`edu-subj-add-${entry.id}`}
                className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
                <Plus size={12} weight="bold"/> add subject
              </button>
              <button onClick={() => setExtractOpen((v) => !v)}
                data-testid={`edu-subj-extract-toggle-${entry.id}`}
                className={`btn-hard rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${
                  extractOpen ? "bg-[var(--brand-ink)] text-white" : "bg-[var(--pastel-yellow)]"
                }`}>
                <MagicWand size={12} weight="fill"/> paste &amp; extract with AI
              </button>
              <label
                data-testid={`edu-subj-doc-upload-label-${entry.id}`}
                className={`btn-hard rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 cursor-pointer ${
                  uploadingDoc ? "bg-[var(--brand-ink)] text-white" : "bg-[var(--pastel-mint)]"
                }`}
                title="upload a transcript / GCSE certificate / A-Level results slip (PDF, DOCX, or photo) — the AI extracts the rows for you">
                <MagicWand size={12} weight="fill"/> {uploadingDoc ? "extracting…" : "extract from document"}
                <input
                  ref={docInputRef}
                  type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
                  disabled={uploadingDoc}
                  onChange={(e) => extractFromDocument(e.target.files?.[0])}
                  data-testid={`edu-subj-doc-upload-${entry.id}`}
                  className="hidden"
                />
              </label>
            </div>

            {extractOpen && (
              <div className="mt-3 card-hard rounded-md bg-[var(--brand-bg)] p-3 space-y-2"
                   data-testid={`edu-subj-extract-panel-${entry.id}`}>
                <p className="text-xs text-[var(--brand-muted)]">
                  paste a messy list (e.g. &quot;Maths A*, English A, Physics B&quot; or &quot;10 GCSEs incl. Maths A*, English A&quot;).
                  the AI turns it into structured rows.
                </p>
                <textarea
                  data-testid={`edu-subj-extract-text-${entry.id}`}
                  value={extractText}
                  onChange={(e) => setExtractText(e.target.value)}
                  placeholder="e.g. Maths A*, English Lit A, Physics B, Biology A, Chemistry B, History C, French B, Art A"
                  rows={3}
                  className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm font-mono-d"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-xs text-[var(--brand-muted)] inline-flex items-center gap-1.5">
                    default level
                    <select
                      data-testid={`edu-subj-extract-level-${entry.id}`}
                      value={extractLevel}
                      onChange={(e) => setExtractLevel(e.target.value)}
                      className="card-hard rounded-md px-2 py-1 bg-white text-xs"
                    >
                      {LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={extractSubjects}
                    disabled={extracting}
                    data-testid={`edu-subj-extract-run-${entry.id}`}
                    className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-60 ml-auto"
                  >
                    <MagicWand size={12} weight="fill"/> {extracting ? "extracting…" : "extract"}
                  </button>
                  <button
                    onClick={() => { setExtractOpen(false); setExtractText(""); }}
                    data-testid={`edu-subj-extract-cancel-${entry.id}`}
                    className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs"
                  >
                    cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Lock-it-in CTA — explicit commit. Auto-save still runs in the background;
              this button gives the user the unmistakable satisfaction of "vaulting" it. */}
          <div className="pt-3 border-t-[1.5px] border-[var(--brand-ink)]/10 flex items-center justify-between gap-3">
            <p className="font-mono-d text-[10px] text-[var(--brand-muted)]">
              auto-saves as you type · click <b>lock it in</b> when you&apos;re done
            </p>
            <button onClick={lockIn} disabled={saving}
              data-testid={`edu-lock-${entry.id}`}
              className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
              <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Section ----------
export default function EducationSection() {
  const [entries, setEntries] = useState([]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/education");
      setEntries(data);
      // Land collapsed by default for tidiness.
      setCollapsed(new Set(data.map((e) => e.id)));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/education", { ...blank(), qualification: "", provider: "" });
      setEntries((cur) => [data, ...cur]);
      // New cards should open expanded so the user can start typing immediately.
      setCollapsed((cur) => {
        const next = new Set(cur);
        next.delete(data.id);
        return next;
      });
      toast.success("New entry added — start filling it in");
    } catch {
      toast.error("Couldn't add entry");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this education entry and all its subjects?")) return;
    try {
      await api.delete(`/education/${id}`);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const clearAll = async () => {
    if (entries.length === 0) return;
    if (!window.confirm(`Delete all ${entries.length} education entries and every subject inside them? This can't be undone.`)) return;
    try {
      const { data } = await api.delete("/vault/clear/education");
      setEntries([]);
      toast.success(`Wiped ${data.deleted} education entries`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  const toggleCollapse = (id, force) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (force === true) next.add(id);
      else if (force === false) next.delete(id);
      else if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allCollapsed = entries.length > 0 && collapsed.size === entries.length;
  const collapseAll  = () => setCollapsed(new Set(entries.map((e) => e.id)));
  const expandAll    = () => setCollapsed(new Set());

  const totalSubjects = useMemo(
    () => entries.reduce((acc, e) => acc + ((e.subjects?.length) || 0), 0),
    [entries]
  );

  return (
    <div className="card-hard rounded-md bg-white p-5 relative overflow-hidden" data-testid="education-section">
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--brand-primary)]"/>
      <div className="flex items-start justify-between gap-3 mb-4 pl-2">
        <div>
          <p className="label-tiny flex items-center gap-1.5 m-0">
            <GraduationCap size={12} weight="duotone" className="text-[var(--brand-primary)]"/> /education
          </p>
          <h2 className="font-display text-2xl tracking-tight mt-1">Education</h2>
          <p className="text-xs text-[var(--brand-muted)] mt-1 max-w-2xl">
            one entry per institution. list every subject as its own row — exactly how job application forms ask for them. changes save automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono-d text-xs text-[var(--brand-muted)]">
            {entries.length} {entries.length === 1 ? "entry" : "entries"} · {totalSubjects} subject{totalSubjects === 1 ? "" : "s"}
          </span>
          {entries.length > 1 && (
            <button onClick={allCollapsed ? expandAll : collapseAll}
              data-testid="edu-collapse-all"
              className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1">
              {allCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {allCollapsed ? "expand all" : "collapse all"}
            </button>
          )}
          {entries.length > 0 && (
            <button onClick={clearAll} data-testid="clear-all-education"
              title={`wipe all ${entries.length} education entries`}
              className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
              <TrashSimple size={13} weight="bold"/>
              <span className="hidden sm:inline">delete all</span>
            </button>
          )}
          <button onClick={create} disabled={creating}
            data-testid="add-education"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            <Plus size={14} weight="bold"/> add education
          </button>
        </div>
      </div>

      {loading ? (
        <p className="font-mono-d text-sm text-[var(--brand-muted)]">loading…</p>
      ) : entries.length === 0 ? (
        <div className="card-hard rounded-md p-8 bg-[var(--brand-bg)] text-center" data-testid="edu-empty">
          <GraduationCap size={32} weight="duotone" className="mx-auto mb-2 text-[var(--brand-muted)]"/>
          <p className="font-display text-lg tracking-tight">no education yet.</p>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            click <b>+ add education</b> above. add one entry per institution — GCSEs, A-Levels, university, courses.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <EducationCard key={e.id}
              entry={e}
              onChange={(next) => setEntries((cur) => cur.map((x) => x.id === next.id ? next : x))}
              onDelete={() => remove(e.id)}
              onCollapseToggle={(force) => toggleCollapse(e.id, force)}
              collapsed={collapsed.has(e.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
